import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete'

import { openNotificationWithMsg } from 'helpers'
import { getAddressComponents } from 'helpers/location'

import Input from './Input'

const LocationInput = ({
  saveLocation,
  name,
  label,
  placeholder,
  defaultAddress,
  onError,
  autoFocus,
  onSave,
  searchType,
  required,
}) => {
  const inputRef = useRef()
  const [address, setAddress] = useState(defaultAddress)

  const handleError = () => openNotificationWithMsg('error', 'Invalid location')
  const handleSelect = async (address, placeId) => {
    if (!address) {
      return
    }

    // NOTE: `placeId` and `suggestion` are null when user hits Enter key with no suggestion item selected.
    if (!placeId && onSave) {
      onSave()
    }
    inputRef.current?.blur()

    setAddress(address)
    try {
      const [locationDataObj] = await geocodeByAddress(address)
      const latLng = await getLatLng(locationDataObj)
      latLng.location = locationDataObj
      const components = getAddressComponents(latLng.location.address_components)
      saveLocation({
        ...latLng,
        components,
      })
    } catch (err) {
      handleError()
    }
  }

  return (
    <PlacesAutocomplete
      value={address}
      onChange={setAddress}
      onSelect={handleSelect}
      onError={handleError}
      debounce={500}
      shouldFetchSuggestions={!!address.length}
      searchOptions={{ types: [searchType] }}
    >
      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
        <div className="flex-1 relative">
          <Input
            ref={inputRef}
            type="text"
            label={label}
            placeholder={placeholder}
            name={name}
            autoFocus={autoFocus}
            required={required}
            onError={onError}
            {...getInputProps({})}
          />
          <div className="absolute z-10 w-full pt-2">
            {loading && <div>Loading...</div>}
            {suggestions.map(suggestion => {
              const className = suggestion.active ? 'suggestion-item--active' : 'suggestion-item'
              // inline style for demonstration purpose
              const style = suggestion.active ? { backgroundColor: '#282D34' } : {}
              return (
                <div
                  {...getSuggestionItemProps(suggestion, {
                    className,
                    style,
                  })}
                  className="p-2 w-full text-base text-white text-opacity-70 font-tomatoGrotesk-reg bg-woodsmokeBlack cursor-pointer hover:text-opacity-100"
                  key={suggestion.placeId}
                >
                  <span>{suggestion.description}</span>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </PlacesAutocomplete>
  )
}

LocationInput.defaultProps = {
  defaultAddress: '',
  label: 'Location',
  placeholder: 'Location',
  searchType: '(cities)',
  required: true,
}

LocationInput.propTypes = {
  defaultAddress: PropTypes.string,
  name: PropTypes.string,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  searchType: PropTypes.string,
  autoFocus: PropTypes.bool,
  onSave: PropTypes.func,
  onError: PropTypes.func,
  saveLocation: PropTypes.func,
}

export default LocationInput
